import { createLogPrefix, logError, type LogTarget, type Unsubscribe } from '@amazon/vinyl-util'
import { type ObservableValue } from '@amazon/vinyl-observable'
import { AuthStatus } from '@amzn/amp-playback'
import { getPlaybackDeps } from '@/blackbolt/playback'
import { clearFeedbackOverlay } from '@/blackbolt/feedback'
import { clearMessageFeedbackOverlay } from '@/blackbolt/chat/messageFeedbackService'
import { clearFollowStateOverlay } from '@/blackbolt/followStateOverlay'
import { FEATURE_GATE_STORAGE_KEYS } from '@/core/featureGate'
import { getCacheManager } from '@/core/offlineCacheManager'
import { navigateTo } from '@/router/router'
import { runSignOutClears } from '@/signOutOverlays'

const target: LogTarget = { logPrefix: createLogPrefix('teardown') }

/**
 * Full sign-out cleanup for the shared-desktop case. Sign-out itself is
 * decoupled: the caller runs `getAuth().signOut()` and the app bootstrap's
 * auth-state listener (`installSignOutTeardown`) invokes this on the
 * signed-in → signed-out transition. Dropping the auth token alone leaves the
 * previous account's in-progress audio playing and its persisted state
 * (session/local storage, cached payloads) live, so the next sign-in on the
 * same machine sees stale content. Beyond the sign-out this also:
 *   1. stops playback + drops the active queue (clearing the playlist
 *      controller nulls `current`, which unloads the shared player),
 *   2. wipes session + local storage so no per-account preference or persisted
 *      token bleeds into the next account on a shared machine, and drops the
 *      in-memory feedback / follow-state overlays (which back those localStorage
 *      keys but survive a `localStorage.clear()` in their own `Map`, so the wipe
 *      alone wouldn't reset them within an SPA sign-out),
 *   3. clears the offline CacheManager (the single instance backs both the
 *      GQL response cache and cached offline files) so the next account never
 *      reads the prior customer's cached payloads,
 *   4. drops the `chat` IndexedDB database (durable chat transcripts, which
 *      the storage clears above don't touch) so no conversation bleeds into
 *      the next account,
 *   5. routes home so no per-account deep link survives the switch.
 *
 * `localStorage.clear()` also drops the device-level preference keys
 * (`explicitLanguageFilter` content filter, `reduceMotion` a11y, `theme`);
 * those losses are intended — a shared machine should not carry one account's
 * device preferences into the next. `deviceInfo` is preserved below: it is
 * this browser's device identity, not an account preference, and regenerating
 * it on every sign-out would present as a brand-new device to Firefly and the
 * DM messaging backend on the very next boot. Feature-gate overrides
 * (`featureGate.*`) are preserved for the same reason: a debug gate is a
 * device/dev preference, not per-account data, so a sign-out triggered while
 * applying a gate change (see `FeatureGatesSection`) must not silently revert
 * it to the shipped default.
 *
 * Every step is independently guarded and best-effort: a throw in any one must
 * not skip the others, and `navigateTo('/')` must always be reached, so each
 * step is wrapped rather than relying on ordering. Idempotent — the auth
 * listener fires it once per sign-out, and every step is safe to re-run.
 * Because auth is already `SIGNED_OUT` by the time this runs, clearing the
 * playlist (which nulls `current`) no longer fires an authed device-streaming
 * report — the concurrency binding skips the release once signed out.
 */
export function teardown(): void {
    try {
        // Clearing the playlist controller nulls `current`, unloading the
        // shared player; the bridged controller can throw on a closed port.
        getPlaybackDeps()?.playlistController.clear()
    } catch (e) {
        // Playback unavailable — continue teardown.
        logError(target, e)
    }

    try {
        sessionStorage.clear()
    } catch (e) {
        // Storage disabled — nothing to clear; continue teardown.
        logError(target, e)
    }
    // Device-level state that isn't per-account data, so it must survive the
    // full-keyspace wipe below: the invite unlock (this browser is allowed to
    // sign in — otherwise sign-out would re-gate a user who already accepted a
    // valid invite), `deviceInfo` (this browser's device identity; see the
    // note above), and every persisted feature-gate override. Save all across
    // the clear and restore after.
    const preservedKeys = ['ampInviteAccepted', 'deviceInfo', ...FEATURE_GATE_STORAGE_KEYS]
    const preserved = new Map<string, string>()
    for (const key of preservedKeys) {
        try {
            const value = localStorage.getItem(key)
            if (value != null) preserved.set(key, value)
        } catch (e) {
            // Storage disabled — nothing to preserve.
            logError(target, e)
        }
    }
    try {
        // Raw `localStorage.clear()` (not per-item `createPersistentItem.remove()`):
        // this intentionally wipes the whole keyspace — every per-account key,
        // known or not — so no persisted value bleeds into the next account on a
        // shared machine. A fixed-key persistent item can't express that.
        localStorage.clear()
    } catch (e) {
        // Storage disabled — nothing to clear; continue teardown.
        logError(target, e)
    }
    for (const [key, value] of preserved) {
        try {
            localStorage.setItem(key, value)
        } catch (e) {
            // Storage disabled — the value just won't persist; continue teardown.
            logError(target, e)
        }
    }

    // Chat transcripts live in IndexedDB (not the storage cleared above), so
    // drop the whole `chat` database. Fire-and-forget with a logged catch:
    // the async delete needn't block the route change, and a shared machine
    // must not carry one account's conversations into the next. Dynamically
    // imported so `idb` stays out of the eager boot bundle.
    import('@/blackbolt/chat/chatStore')
        .then((chatStore) => chatStore.deleteDatabase())
        .catch((e) => logError(target, e))

    // The Bedrock provider persists its wire transcript in a sibling
    // `bedrockChat` database; wipe it on the same sign-out so a shared machine
    // doesn't carry one account's conversation memory into the next.
    import('@/bedrockAgent/bedrockTranscriptStore')
        .then((store) => store.deleteDatabase())
        .catch((e) => logError(target, e))

    // The feedback / follow overlays keep an in-memory Map that outlives
    // `localStorage.clear()` on an SPA sign-out, so clear them explicitly.
    try {
        clearFeedbackOverlay()
    } catch (e) {
        // Overlay unavailable — continue teardown.
        logError(target, e)
    }
    try {
        clearMessageFeedbackOverlay()
    } catch (e) {
        // Overlay unavailable — continue teardown.
        logError(target, e)
    }
    try {
        clearFollowStateOverlay()
    } catch (e) {
        // Overlay unavailable — continue teardown.
        logError(target, e)
    }

    // Drop the in-memory half of every persisted optimistic overlay (track /
    // album / artist likes+follows). `localStorage.clear()` above wipes their
    // durable half, but each overlay's in-memory Map outlives it on an SPA
    // sign-out. Decoupled via a registry so teardown imports no domain module
    // (avoids an `auth ↔ track` biref); each is independently guarded.
    runSignOutClears()

    try {
        // A failed cache clear must not block teardown — the next sign-in
        // re-fetches fresh regardless — but it must still be reported: this wipe
        // is what stops the next account on a shared machine reading the previous
        // customer's cached payloads, so a silent failure is a privacy miss.
        getCacheManager()
            .clear()
            .catch((e) => logError(target, e))
    } catch (e) {
        // CacheManager unavailable — continue teardown.
        logError(target, e)
    }

    navigateTo('/')
}

/**
 * Bind sign-out teardown to auth state: run `onSignedOut` (defaults to
 * `teardown`) on the signed-in → signed-out transition only. Guarding on the
 * prior status being `SIGNED_IN` skips both the initial anon boot
 * (`PENDING → SIGNED_OUT`) and any repeat `SIGNED_OUT` emit, so teardown runs
 * exactly once per sign-out.
 */
export function installSignOutTeardown(
    status$: ObservableValue<AuthStatus>,
    onSignedOut: () => void = teardown
): Unsubscribe {
    let previous = status$.value
    return status$.onData((status) => {
        if (status === AuthStatus.SIGNED_OUT && previous === AuthStatus.SIGNED_IN) onSignedOut()
        previous = status
    })
}
